import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Container,
  Paper,
  Chip,
} from "@mui/material";
import {
  BugReport,
  TipsAndUpdates,
  Feedback as FeedbackIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";

import CustomTable from "../../components/common/CustomTable";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import CustomHeader from "../../components/common/CustomHeader";
import SearchBox from "../../components/common/CustomSearchBar";
import { feedbackApi } from "../../api/feedbackApi";
import CustomSnackbar from "../../components/common/CustomSnackbar";
import { maxWidth } from "@mui/system";

const FeedbackListPage = () => {
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  /* ---------------- FETCH ---------------- */

  useEffect(() => {
    const fetchFeedback = async () => {
      try {
        setLoading(true);

        const response = await feedbackApi.getAll();
        const { status, message, data } = response || {};

        if (status === 0) {
          showSnackbar(
            "Network error. Please check your internet connection.",
            "warning"
          );
          setFeedback([]);
          return;
        }

        if (status >= 500) {
          showSnackbar(
            message || "Server error while fetching feedback data.",
            "error"
          );
          setFeedback([]);
          return;
        }

        if (status === 200 && Array.isArray(data)) {
          setFeedback(data);
          return;
        }

        showSnackbar("Unexpected response from server.", "error");
        setFeedback([]);
      } catch (error) {
        console.error("Error fetching feedback:", error);
        showSnackbar("Something went wrong while loading feedback.", "error");
        setFeedback([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFeedback();
  }, []);

  /* ---------------- SEARCH ---------------- */

  const filteredFeedback = useMemo(() => {
    const q = search.toLowerCase();

    return feedback.filter((f) =>
      [
        f.feature_name,
        f.feedback_type,
        f.status,
        f.comments,
      ].some((field) => field?.toLowerCase().includes(q))
    );
  }, [feedback, search]);

  const hasFeedback = feedback.length > 0;

  /* ---------------- TABLE COLUMNS ---------------- */

  const columns = [
    {
      label: "Feature",
      field: "feature_name",
    },
    {
      label: "Comments",
      field: "comments",
      render: (val) => <Typography>{val || "—"}</Typography>,
    },
    {
      label: "Type",
      field: "feedback_type",
      render: (val) => {
        const iconMap = {
          Bug: <BugReport fontSize="small" />,
          Improvement: <TipsAndUpdates fontSize="small" />,
          Feedback: <FeedbackIcon fontSize="small" />,
        };

        const colorMap = {
          Bug: "error",
          Improvement: "warning",
          Feedback: "success",
        };

        return (
          <Chip
            icon={iconMap[val]}
            size="small"
            label={val}
            color={colorMap[val] || "default"}
          />
        );
      },
    },
    {
      label: "Status",
      field: "status",
      render: (val) => {
        const colorMap = {
          Open: "warning",
          Closed: "success",
          Resolved: "success",
          Pending: "info",
        };

        return (
          <Chip
            size="small"
            variant="outlined"
            label={val}
            color={colorMap[val] || "default"}
          />
        );
      },
    },
    {
      label: "Created At",
      field: "created_at",
      render: (val) =>
        dayjs(val).format("DD MMM YYYY, hh:mm A"),
    },
  ];

  /* ---------------- RENDER ---------------- */

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <CustomHeader title="Feedback Report" />

      <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
        {loading ? (
          <SkeletonLoader type="feedback" rows={6} />
        ) : !hasFeedback ? (
          /* ✅ No feedback at all */
          <Paper
            elevation={3}
            sx={{
              mt: 4,
              p: 6,
              textAlign: "center",
              borderRadius: 3,
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              No Feedback Available
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              No feedback submitted yet. Once users provide feedback, it will
              appear here.
            </Typography>
          </Paper>
        ) : (
          /* ✅ Feedback exists → table always visible */
          <Box sx={{ width: "100%" }}>
            {/* Search */}
            <Box sx={{ mb: 2, width: "100%", }}>
              <SearchBox
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClear={() => setSearch("")}
                placeholder="Search by feature, status, type, comments..."
                minWidth={"100%"}
                height={38}
                fontSize="0.85rem"
              />
            </Box>

            <Typography sx={{ mb: 1, color: "gray" }}>
              Showing {filteredFeedback.length} feedback entries
            </Typography>

            {/* Table always rendered, even if rows are empty */}
            <Box
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                bgcolor: "#111",
                boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
              }}
            >
              <CustomTable
                columns={columns}
                rows={filteredFeedback} // empty array → blank table body
                emptyMessage="No Feedback Found!"
                sx={{
                  "& .MuiTableCell-root": {
                    color: "white",
                    borderBottom:
                      "1px solid rgba(255,255,255,0.1)",
                  },
                  "& .MuiTableHead-root": {
                    backgroundColor: "#222",
                  },
                  "& .MuiTableHead-root .MuiTableCell-root": {
                    color: "lightgray",
                    fontWeight: "bold",
                  },
                  "& .MuiTableRow-root:hover": {
                    backgroundColor:
                      "rgba(255,255,255,0.05)",
                  },
                }}
              />
            </Box>
          </Box>
        )}
      </Container>

      {/* Snackbar */}
      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() =>
          setSnackbar((s) => ({ ...s, open: false }))
        }
      />
    </Box>
  );
};

export default FeedbackListPage;
